// This is a copy from "../Skeleton/Skeleton.tsx" for gradual refactoring

import { useMemo } from 'react';

import { useAtomValue } from 'jotai';
import * as THREE from 'three';

import type { IMeasurement } from '@core';
import { activeData3dAtom, comparisonData3dAtom, currentFrameAtom, syncedFrameAtom } from '@store';

import { getKeypointName, getSafeSkeleton } from '../helpers';
import { BoneMesh } from '../Skeleton/BoneMesh';
import { HeadMesh } from '../Skeleton/HeadMesh';
import { Joint } from '../Skeleton/Joint';
import { SpineMesh } from '../Skeleton/SpineMesh';

import type { FrameFirstSkeleton } from '../skeletonUtils';
import type { BoneMapping, KeypointIdToName } from '../types';

export const GOLF_BALL_RADIUS = 0.02135;

export const useBallOrigin = (ballTranslation: [number, number, number] | undefined) => {
    const originOffset: [number, number, number] = useMemo(() => {
        if (!ballTranslation) return [0, 0, 0];
        return [-ballTranslation[0], -ballTranslation[1], -ballTranslation[2]];
    }, [ballTranslation]);
    return originOffset;
};

const SPINE_KEYPOINTS: readonly string[] = ['pelvis', 'upper_torso'];

/**
 * Checks if a bone connects pelvis to upper_torso (the spine bone).
 * This bone is rendered by SpineMesh instead of BoneMesh.
 */
function isSpineBone(
    bone: { start_point_id: number; end_point_id: number },
    keypointIdToName: KeypointIdToName,
): boolean {
    const name1 = getKeypointName(bone.start_point_id, keypointIdToName);
    const name2 = getKeypointName(bone.end_point_id, keypointIdToName);
    return SPINE_KEYPOINTS.includes(name1) && SPINE_KEYPOINTS.includes(name2) && name1 !== name2;
}

type SkeletonMeshConfig = {
    frame: number;
    skeleton: FrameFirstSkeleton;
    boneMapping: BoneMapping;
    keypointIdToName: KeypointIdToName;
    targetPoints: string[];
    frozen?: boolean;
    isComparison?: boolean;
    uniquePointIds: number[];
    measurements?: IMeasurement[];
};

export function createSkeletonMesh({
    frame,
    skeleton,
    boneMapping,
    keypointIdToName,
    targetPoints = [],
    frozen = false,
    isComparison = false,
    uniquePointIds,
    measurements,
}: SkeletonMeshConfig) {
    const translucentPelvis = SPINE_KEYPOINTS.every((p) => targetPoints.includes(p));

    return (
        <group>
            {uniquePointIds.map((pointId) => {
                const point = getSafeSkeleton(skeleton, frame, pointId);
                const pointName = getKeypointName(pointId, keypointIdToName);
                const highlighted = targetPoints.includes(pointName);

                if (!point) {
                    return null;
                }

                return (
                    <Joint
                        key={pointId}
                        pointId={pointId}
                        position={new THREE.Vector3(...point)}
                        keypointIdToName={keypointIdToName}
                        isHighlighted={highlighted}
                        isTranslucent={frozen}
                        isComparison={isComparison}
                    />
                );
            })}

            {boneMapping
                .filter((bone) => !isSpineBone(bone, keypointIdToName))
                .map((keypoint, idx) => {
                    const keypointName1 = getKeypointName(keypoint.start_point_id, keypointIdToName);
                    const keypointName2 = getKeypointName(keypoint.end_point_id, keypointIdToName);
                    const isTranslucent = targetPoints.includes(keypointName1) || targetPoints.includes(keypointName2);

                    const boneName = `${keypointName1}__${keypointName2}`;

                    const keypoint1 = getSafeSkeleton(skeleton, frame, keypoint.start_point_id);
                    const keypoint2 = getSafeSkeleton(skeleton, frame, keypoint.end_point_id);

                    if (!keypoint1 || !keypoint2) {
                        return null;
                    }

                    return (
                        <BoneMesh
                            key={`bone-${idx}`}
                            name={boneName}
                            point1={new THREE.Vector3(...keypoint1)}
                            point2={new THREE.Vector3(...keypoint2)}
                            isTranslucent={isTranslucent}
                            isFrozen={frozen}
                            isComparison={isComparison}
                        />
                    );
                })}

            <SpineMesh
                frame={frame}
                skeleton={skeleton}
                keypointIdToName={keypointIdToName}
                measurements={measurements}
                isTranslucent={translucentPelvis || frozen}
                isComparison={isComparison}
            />

            <HeadMesh
                frame={frame}
                skeleton={skeleton}
                keypointIdToName={keypointIdToName}
                isTranslucent={frozen}
                isComparison={isComparison}
            />
        </group>
    );
}

type SkeletonProps = {
    origin?: string;
    targetPoints?: string[];
};

const CURRENT = 'current';

export function Skeleton({ origin = CURRENT, targetPoints = [] }: SkeletonProps) {
    const data = useAtomValue(origin === CURRENT ? activeData3dAtom : comparisonData3dAtom);
    const resolvedFrame = useAtomValue(origin === CURRENT ? currentFrameAtom : syncedFrameAtom);

    const uniquePointIds = useMemo(() => {
        const uniquePoints = new Set<number>();
        data?.bones.forEach((bone) => {
            uniquePoints.add(bone.end_point_id);
            uniquePoints.add(bone.start_point_id);
        });
        return Array.from(uniquePoints);
    }, [data?.bones]);

    if (!data) {
        return null;
    }

    const { skeleton, bones = [], keypointIdToName, measurements } = data;

    return (
        <group>
            {createSkeletonMesh({
                frame: resolvedFrame,
                skeleton,
                boneMapping: bones,
                keypointIdToName,
                targetPoints,
                frozen: false,
                // design quirk, is comparison applies transparency
                isComparison: false,
                uniquePointIds,
                measurements,
            })}
        </group>
    );
}
